/**
 * Logica pura dos sinais de autenticacao e do remetente (WYM-09, ADR 0051).
 *
 * Nada aqui prova que um remetente e legitimo: SPF, DKIM e DMARC dizem que o
 * dominio autorizou o envio, nao que a pessoa e quem diz ser.
 */

export type Veredicto =
  | 'pass'
  | 'fail'
  | 'softfail'
  | 'neutral'
  | 'none'
  | 'policy'
  | 'temperror'
  | 'permerror'
  | 'unknown';

export interface ResultadosDeAutenticacao {
  spf: Veredicto | null;
  dkim: Veredicto | null;
  dmarc: Veredicto | null;
}

export type NivelDeAutenticacao = 'verificado' | 'atencao' | 'desconhecido';

export function nivelDeAutenticacao(r: ResultadosDeAutenticacao | null): NivelDeAutenticacao {
  if (r === null) return 'desconhecido';
  if ([r.spf, r.dkim, r.dmarc].some((v) => v === 'fail' || v === 'softfail' || v === 'permerror')) {
    return 'atencao';
  }
  // So DMARC prova alinhamento com o `From`. SPF e DKIM `pass` sozinhos podem
  // ser do dominio do atacante (`d=evil.com`), com `From` de outro (A1).
  return r.dmarc === 'pass' ? 'verificado' : 'desconhecido';
}

const ROTULOS: Record<Veredicto, string> = {
  pass: 'passou',
  fail: 'falhou',
  softfail: 'falha branda',
  neutral: 'neutro',
  none: 'sem registro',
  policy: 'bloqueado por política',
  temperror: 'erro temporário',
  permerror: 'erro permanente',
  unknown: 'não reconhecido',
};

export function rotuloDoVeredicto(v: Veredicto | null): string {
  return v === null ? 'não informado' : ROTULOS[v];
}

interface Endereco {
  name: string;
  address: string;
}

const ENDERECO_NO_TEXTO = /[a-z0-9._%+-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)+/gi;

function dominio(endereco: string): string {
  return endereco.slice(endereco.lastIndexOf('@') + 1).toLowerCase();
}

export interface AvisosDoRemetente {
  /** O nome de exibicao traz um e-mail diferente do real: golpe classico. */
  nomeImitaEndereco: string | null;
  /** Reply-To em outro dominio: a resposta iria para um lugar diferente. */
  respostaParaOutroDominio: string[];
}

export function avisosDoRemetente(
  de: Endereco | null,
  replyTo: readonly Endereco[],
): AvisosDoRemetente {
  const vazio: AvisosDoRemetente = { nomeImitaEndereco: null, respostaParaOutroDominio: [] };
  if (de === null) return vazio;

  const real = de.address.toLowerCase();
  // NFKC: "suporte＠paypal.com" (arroba e letras de largura total) e lido como e-mail.
  const imitado =
    (de.name.normalize('NFKC').match(ENDERECO_NO_TEXTO) ?? [])
      .map((e) => e.toLowerCase())
      .find((e) => e !== real) ?? null;

  const outros = replyTo
    .map((r) => r.address.toLowerCase())
    .filter((r) => r !== '' && dominio(r) !== dominio(real));

  return { nomeImitaEndereco: imitado, respostaParaOutroDominio: [...new Set(outros)] };
}

const TIPOS: Array<[RegExp, string]> = [
  [/^application\/pdf$/i, 'PDF'],
  [/^image\/(.+)$/i, 'Imagem'],
  [/(word|\.document)/i, 'Documento'],
  [/(excel|spreadsheet)/i, 'Planilha'],
  [/(powerpoint|presentation)/i, 'Apresentação'],
  [/^application\/(zip|x-7z|vnd\.rar|gzip)/i, 'Compactado'],
  [/^text\/(.+)$/i, 'Texto'],
];

/** Rotulo curto do tipo, para a linha de tamanho do anexo. Nunca confia no nome. */
export function rotuloDoTipo(contentType: string, filename: string): string {
  for (const [re, rotulo] of TIPOS) if (re.test(contentType)) return rotulo;
  const ext = filename.includes('.') ? filename.split('.').pop()!.toUpperCase() : '';
  return ext !== '' && ext.length <= 5 ? ext : 'Arquivo';
}
